/**
 * Universal StickySearchBar Component
 * 
 * This component provides a universal sticky search bar that appears when the main search form
 * scrolls out of view. It works on hotel and excursion list pages. Car rental uses its own fleet search.
 * 
 * Features:
 * - Smooth transition: Pre-reserves space to prevent layout jumps
 * - Universal design: Same styling and behavior across all service types
 * - Service-specific forms: Form fields are conditionally rendered based on serviceType
 * - Responsive: Adapts to different screen sizes
 * - SEO-friendly: Uses URL parameters for search criteria
 * 
 * Usage:
 * - Automatically detects service type from pathname
 * - Looks for search form with class 'hero-search-section'
 * - Appears when form scrolls out of view
 * - Form fields can be customized per service type
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useScrollEvent, useRegionsList } from '@/hooks';
import { useSiteTrip } from '@/states';
import { useLocation, useNavigate, useSearchParams } from '@/compat/react-router-dom';
import clsx from 'clsx';
import { BsCalendar, BsClock, BsGeoAlt } from 'react-icons/bs';
import { FaBed, FaBus } from 'react-icons/fa';
import { SelectFormInput, LanguageSelect } from '@/components';
import Flatpicker from '@/components/Flatpicker';
import { excursionsService, accommodationsService, hotelEngineService } from '@/api/services';
import { isHotelEngineSellable } from '@/utils/hotelEngineSell';

const StickySearchBar = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { scrollY } = useScrollEvent();
  const { visitDate: globalVisitDate } = useSiteTrip();
  const stickyBarRef = useRef(null);
  const [isSticky, setIsSticky] = useState(false);
  
  // Check if the search form has scrolled out of view
  useEffect(() => {
    const checkSticky = () => {
      const searchForm = document.querySelector('.hero-search-section');
      if (!searchForm) {
        setIsSticky(false);
        return;
      }
      
      const rect = searchForm.getBoundingClientRect();
      const headerEl = document.querySelector('.site-header-gyg');
      const topBarHeight = headerEl?.offsetHeight ?? (window.innerWidth >= 992 ? 120 : 96);
      
      // Show sticky bar only when the search form's bottom has completely scrolled past the top bar
      // This ensures no overlap between the sticky bar and the original form
      setIsSticky(rect.bottom <= topBarHeight);
    };
    
    // Check on scroll and resize
    checkSticky();
    window.addEventListener('scroll', checkSticky, { passive: true });
    window.addEventListener('resize', checkSticky, { passive: true });
    
    return () => {
      window.removeEventListener('scroll', checkSticky);
      window.removeEventListener('resize', checkSticky);
    };
  }, [scrollY, pathname]); // Re-run when pathname changes to find new search form
  
  // All positioning is now handled via pure CSS - no JavaScript interference
  
  // Determine which service type we're on
  let serviceType = null;
  let serviceLabel = '';
  let serviceIcon = null;
  const partnerListingMatch = pathname.match(/^\/partner\/([^/]+)\/excursions\/?$/);
  const partnerListingSlug = partnerListingMatch ? partnerListingMatch[1] : null;
  const excursionsListingPath = partnerListingSlug
    ? `/partner/${partnerListingSlug}/excursions`
    : '/excursions';

  if (pathname.startsWith('/hotels')) {
    serviceType = 'hotels';
    serviceLabel = 'Hotels';
    serviceIcon = <FaBed size={18} />;
  } else if (pathname.startsWith('/excursions') || partnerListingSlug) {
    serviceType = 'excursions';
    serviceLabel = 'Excursions';
    serviceIcon = <FaBus size={18} />;
  }

  const shouldRender =
    Boolean(serviceType) &&
    !pathname.match(/^\/excursions\/[^/]+$/) &&
    !pathname.match(/^\/partner\/[^/]+\/excursions\/[^/]+/) &&
    !pathname.match(/^\/hotels\/[^/]+$/) &&
    !pathname.match(/^\/car-rental\/[^/]+$/) &&
    !pathname.match(/^\/car-rental\/model\//);

  const parseYmd = useCallback((value) => {
    if (!value) return null;
    const parsed = new Date(`${value}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, []);

  const formatYmd = useCallback((date) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, []);

  // Initialize form data from URL params
  const getInitialFormData = useCallback(() => {
    const dateFrom = parseYmd(searchParams.get('dateFrom'));
    const dateTo = parseYmd(searchParams.get('dateTo'));
    let dateValue = parseYmd(searchParams.get('date'));
    if (!dateValue && !dateFrom && globalVisitDate) {
      dateValue = parseYmd(globalVisitDate);
    }

    return {
      date: dateValue || dateFrom,
      dateFrom: dateFrom || dateValue,
      dateTo: dateTo || dateFrom || dateValue,
      language: searchParams.get('language') || null,
      duration: searchParams.get('duration') || null,
      pickupArea: searchParams.get('pickupArea') || null,
    };
  }, [searchParams, globalVisitDate, parseYmd]);

  const [formData, setFormData] = useState(getInitialFormData());
  const [regions, setRegions] = useState([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const { data: regionsData, loading: regionsLoading, error: regionsError } = useRegionsList();

  // Sync form data when URL params change
  useEffect(() => {
    setFormData(getInitialFormData());
  }, [getInitialFormData]);

  // Fetch regions for excursions (only those with hotels that have pickups)
  useEffect(() => {
    if (serviceType === 'excursions') {
      setLoadingRegions(true);
      
      if (regionsLoading) {
        setLoadingRegions(true);
        return;
      }

      if (regionsError) {
        setRegions([]);
        setLoadingRegions(false);
        return;
      }

      // Fetch pickups and hotels in parallel
      Promise.all([
        excursionsService.getAllPickups().catch(() => ({ data: { data: [] } })),
        accommodationsService.getAll().catch(() => ({ data: { data: [] } }))
      ])
        .then(([pickupsResponse, hotelsResponse]) => {
          const allRegions = Array.isArray(regionsData) ? regionsData : [];
          const pickups = pickupsResponse.data?.data || pickupsResponse.data || [];
          const hotels = hotelsResponse.data?.data || hotelsResponse.data || [];

          // Get unique hotel IDs that have pickups
          const hotelIdsWithPickups = new Set(
            pickups
              .map(pickup => String(pickup.acc_id || pickup.accommodation_id || pickup.hotel_id || ''))
              .filter(id => id && id !== '')
          );

          // Get unique region IDs that have hotels with pickups
          const regionIdsWithPickups = new Set(
            hotels
              .filter(hotel => {
                const hotelId = String(hotel.id);
                return hotelIdsWithPickups.has(hotelId);
              })
              .map(hotel => {
                // Check all possible region ID fields
                return String(
                  hotel.region_id || 
                  hotel.destination_region_id || 
                  hotel.region?.id || 
                  hotel.destination?.region_id || 
                  ''
                );
              })
              .filter(id => id && id !== '')
          );

          // Filter regions to only show those with hotels that have pickups
          const filteredRegions = allRegions.filter(region => {
            const regionId = String(region.id);
            return regionIdsWithPickups.has(regionId);
          });

          setRegions(filteredRegions);
        })
        .catch(() => setRegions([]))
        .finally(() => setLoadingRegions(false));
    }
  }, [serviceType, regionsData, regionsLoading, regionsError]);

  // Update URL params when form data changes
  const updateField = useCallback((field, value) => {
    setFormData((prevFormData) => {
      const newFormData = { ...prevFormData, [field]: value };
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('flexDates');

      if (serviceType === 'excursions') {
        const fromYmd = formatYmd(newFormData.dateFrom);
        const toYmd = formatYmd(newFormData.dateTo) || fromYmd;
        if (fromYmd) {
          nextParams.set('date', fromYmd);
          nextParams.set('dateFrom', fromYmd);
          if (toYmd) nextParams.set('dateTo', toYmd);
        } else {
          nextParams.delete('date');
          nextParams.delete('dateFrom');
          nextParams.delete('dateTo');
        }
      } else {
        const dateYmd = formatYmd(newFormData.date);
        if (dateYmd) nextParams.set('date', dateYmd);
        else nextParams.delete('date');
      }

      if (newFormData.language && newFormData.language !== '-1') {
        nextParams.set('language', newFormData.language);
      } else {
        nextParams.delete('language');
      }
      if (newFormData.duration && newFormData.duration !== '-1') {
        nextParams.set('duration', newFormData.duration);
      } else {
        nextParams.delete('duration');
      }
      if (newFormData.pickupArea && newFormData.pickupArea !== '-1') {
        nextParams.set('pickupArea', newFormData.pickupArea);
      } else {
        nextParams.delete('pickupArea');
      }

      if (partnerListingSlug) {
        const query = nextParams.toString();
        navigate(`${excursionsListingPath}${query ? `?${query}` : ''}`, { replace: true });
      } else {
        setSearchParams(nextParams, { replace: true });
      }
      return newFormData;
    });
  }, [excursionsListingPath, formatYmd, navigate, partnerListingSlug, searchParams, serviceType, setSearchParams]);

  // Render sticky search bar for all service types
  // Form fields will be rendered based on serviceType

  // Add/remove padding from main content when sticky bar is active
  // Pre-reserve space from page load to prevent any layout jumps during scroll
  useEffect(() => {
    const main = document.querySelector('main');
    if (!main) return;
    
      const headerEl = document.querySelector('.site-header-gyg');
      const topBarHeight = headerEl?.offsetHeight ?? (window.innerWidth >= 992 ? 120 : 96);
      const stickyBarHeight = 72; // Increased height (form padding + element heights)
      const paddingNeeded = topBarHeight + stickyBarHeight;
    
    // Check if form exists and apply padding immediately
    const applyPadding = () => {
      const searchForm = document.querySelector('.hero-search-section');
      if (searchForm) {
        // Form exists - always reserve space from the start
        // This prevents any layout jumps when sticky bar appears
        if (main.style.paddingTop !== `${paddingNeeded}px`) {
          main.style.paddingTop = `${paddingNeeded}px`;
          // Use smooth transition (though ideally padding won't change)
          main.style.transition = 'padding-top 0.25s cubic-bezier(0.4, 0, 0.2, 1)';
        }
      } else {
        // No form = remove padding
        if (main.style.paddingTop !== '') {
          main.style.paddingTop = '';
        }
      }
    };
    
    // Apply padding immediately when component mounts
    // Use setTimeout to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      applyPadding();
    }, 0);
    
    // Also check on scroll/resize to handle dynamic content
    window.addEventListener('scroll', applyPadding, { passive: true });
    window.addEventListener('resize', applyPadding, { passive: true });
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('scroll', applyPadding);
      window.removeEventListener('resize', applyPadding);
      if (main) {
        main.style.paddingTop = '';
      }
    };
  }, [pathname]); // Re-run when pathname changes

  // Render to document.body to avoid any parent container interference
  const stickyBarContent = (
    <div 
      ref={stickyBarRef}
      className={clsx('sticky-search-bar', {
        'sticky-search-bar-active': isSticky
      })}
    >
      <div className="sticky-search-wrapper">
        <Container>
          <Row>
              <Col xs={11} className="mx-auto" style={{ maxWidth: '91.666667%' }}>
              <form className="w-100">
                <Row className="g-2 g-md-3 align-items-center">
                  {/* Service Label */}
                  <Col xs="auto" className="d-none d-md-flex align-items-center">
                    <div className="d-flex align-items-center gap-2 text-primary">
                      {serviceIcon}
                      <span className="fw-semibold small">{serviceLabel}</span>
                    </div>
                  </Col>

                  {/* Date */}
                  <Col xs={12} sm={6} md={3} lg="auto" className="mb-2 mb-sm-0" style={{ flex: '0.8 1 0', minWidth: serviceType === 'excursions' ? '168px' : '120px' }}>
                    <div className="d-flex align-items-center gap-2" style={{ position: 'relative' }}>
                      <BsCalendar className="text-primary flex-shrink-0" size={22} />
                      <div className="flex-grow-1" style={{ minWidth: 0, position: 'relative' }}>
                        {serviceType === 'excursions' ? (
                          <Flatpicker
                            placeholder="Dates"
                            value={[formData.dateFrom, formData.dateTo].filter(Boolean)}
                            options={{
                              mode: 'range',
                              dateFormat: 'j M',
                              closeOnSelect: false,
                              minDate: 'today',
                            }}
                            getValue={(selectedDates) => {
                              const range = Array.isArray(selectedDates)
                                ? selectedDates.filter((item) => item instanceof Date && !Number.isNaN(item.getTime()))
                                : [];
                              const dateFrom = range[0] || null;
                              const dateTo = range[1] || range[0] || null;
                              setFormData((prev) => {
                                const next = { ...prev, dateFrom, dateTo, date: dateFrom };
                                const nextParams = new URLSearchParams(searchParams);
                                nextParams.delete('flexDates');
                                const fromYmd = formatYmd(dateFrom);
                                const toYmd = formatYmd(dateTo) || fromYmd;
                                if (fromYmd) {
                                  nextParams.set('date', fromYmd);
                                  nextParams.set('dateFrom', fromYmd);
                                  if (toYmd) nextParams.set('dateTo', toYmd);
                                }
                                if (partnerListingSlug) {
                                  const query = nextParams.toString();
                                  navigate(`${excursionsListingPath}${query ? `?${query}` : ''}`, { replace: true });
                                } else {
                                  setSearchParams(nextParams, { replace: true });
                                }
                                return next;
                              });
                            }}
                          />
                        ) : (
                          <Flatpicker
                            placeholder="Any Date"
                            key={formData.date ? formData.date.toString() : 'no-date'}
                            value={formData.date || null}
                            options={{
                              dateFormat: 'd M',
                              defaultDate: formData.date || null
                            }}
                            getValue={(selectedDates) => {
                              if (selectedDates && selectedDates.length > 0 && selectedDates[0]) {
                                updateField('date', selectedDates[0]);
                              } else if (Array.isArray(selectedDates) && selectedDates.length === 0) {
                                updateField('date', null);
                              } else if (selectedDates && !Array.isArray(selectedDates)) {
                                updateField('date', selectedDates);
                              } else {
                                updateField('date', null);
                              }
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </Col>
              
              {/* Language */}
              <Col xs={12} sm={6} md={3} lg="auto" className="mb-2 mb-sm-0 d-none d-sm-flex" style={{ flex: '1 1 0', minWidth: '160px' }}>
                <div className="d-flex align-items-center gap-2 w-100">
                  <LanguageSelect
                    value={formData.language || null}
                    onChange={(value) => {
                      updateField('language', value === '-1' || !value ? null : value);
                    }}
                    placeholder="Language"
                    showIcon={true}
                    iconSize={28}
                    showLabel={false}
                    className="form-select js-choice flex-grow-1"
                    data-search-enabled="true"
                  />
                </div>
              </Col>
              
                  {/* Pickup Area - Only for excursions */}
              {serviceType === 'excursions' && (
                <Col xs={12} sm={6} md={2} lg="auto" className="mb-2 mb-sm-0 d-none d-md-flex" style={{ flex: '1 1 0', minWidth: '160px' }}>
                  <div className="d-flex align-items-center gap-2 w-100">
                    <BsGeoAlt className="text-primary flex-shrink-0" size={28} />
                    <SelectFormInput 
                      className="form-select js-choice flex-grow-1" 
                      defaultValue={formData.pickupArea || -1}
                      onChange={(value) => {
                        updateField('pickupArea', value === '-1' || !value ? null : value);
                      }}
                      disabled={loadingRegions}
                    >
                      <option value={-1}>Any area</option>
                      {regions.map(region => (
                        <option key={region.id} value={region.id}>{region.name}</option>
                      ))}
                    </SelectFormInput>
                  </div>
                </Col>
              )}
              
              {/* Duration - Only for excursions */}
              {serviceType === 'excursions' && (
                <Col xs={12} sm={12} md={6} lg={4} xl="auto" className="mb-2 mb-sm-0 d-none d-lg-flex">
                  <div className="d-flex align-items-center gap-2">
                    <BsClock className="text-primary flex-shrink-0" size={28} />
                    <div className="btn-group" role="group">
                      <button
                        type="button"
                        className={`btn btn-sm btn-light mb-0 ${formData.duration === 'full-day' ? 'btn-primary-check active' : 'btn-primary-soft-check'}`}
                        onClick={() => updateField('duration', formData.duration === 'full-day' ? null : 'full-day')}
                      >
                        Full Day
                      </button>
                      <button
                        type="button"
                        className={`btn btn-sm btn-light mb-0 ${formData.duration === 'half-day' ? 'btn-primary-check active' : 'btn-primary-soft-check'}`}
                        onClick={() => updateField('duration', formData.duration === 'half-day' ? null : 'half-day')}
                      >
                        Half Day
                      </button>
                    </div>
                  </div>
                </Col>
              )}
              
              {serviceType === 'hotels' && (
                <Col xs="auto">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm mb-0"
                    onClick={async () => {
                      try {
                        const res = await hotelEngineService.getStatus();
                        const status = res?.data?.data || res?.data;
                        if (!isHotelEngineSellable(status)) {
                          navigate('/hotels');
                          return;
                        }
                      } catch {
                        navigate('/hotels');
                        return;
                      }
                      const checkIn = formData.date instanceof Date && !Number.isNaN(formData.date.getTime())
                        ? `${formData.date.getFullYear()}-${String(formData.date.getMonth() + 1).padStart(2, '0')}-${String(formData.date.getDate()).padStart(2, '0')}`
                        : '';
                      const params = new URLSearchParams();
                      if (checkIn) {
                        const out = new Date(`${checkIn}T12:00:00`);
                        out.setDate(out.getDate() + 3);
                        params.set('check_in', checkIn);
                        params.set('check_out', `${out.getFullYear()}-${String(out.getMonth() + 1).padStart(2, '0')}-${String(out.getDate()).padStart(2, '0')}`);
                      }
                      navigate(`/hotels/search${params.toString() ? `?${params.toString()}` : ''}`);
                    }}
                  >
                    Search stays
                  </button>
                </Col>
              )}
                </Row>
      </form>
    </Col>
  </Row>
</Container>
      </div>
    </div>
  );

  if (!shouldRender) return null;
  return stickyBarContent;
};

export default StickySearchBar;
